import React, {
  useRef,
  Fragment,
  useEffect,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {
  useTable,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useExpanded,
  usePagination,
} from 'react-table';
import debounce from 'lodash/debounce';

import { useTranslation } from 'react-i18next';
import {
  Table,
  Row,
  Label,
  Col,
  Input,
  UncontrolledTooltip,
  Button,
  Pagination,
  PaginationItem,
  Spinner,
  Form,
  PaginationLink,
  FormGroup,
} from 'reactstrap';
import { delApi } from 'api/crud';
import JobListGlobalFilter from '../../components/Common/GlobalSearchFilter';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import DeleteModal from './DeleteModal';
import Spinners from 'components/Common/Spinner';
import { reverseApi } from 'api/transaction';
import { SelectField } from 'components/Common/fields';
import './table.scss';

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  isJobListGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <React.Fragment>
      <Col md={4}>
        <div className="search-box me-xxl-2 my-3 my-xxl-0 d-inline-block">
          <div className="position-relative">
            <label htmlFor="search-bar-0" className="search-label">
              <span id="search-bar-0-label" className="sr-only">
                Search this table
              </span>
              <input
                onChange={(e) => {
                  setValue(e.target.value);
                  onChange(e.target.value);
                }}
                id="search-bar-0"
                type="text"
                className="form-control"
                placeholder={`${count} records...`}
                value={value || ''}
              />
            </label>
            <i className="bx bx-search-alt search-icon"></i>
          </div>
        </div>
      </Col>
      {isJobListGlobalFilter && (
        <JobListGlobalFilter setGlobalFilter={setGlobalFilter} />
      )}
    </React.Fragment>
  );
}

const itemsPerPage = 10;

const TableContainer = ({
  addText = 'Add New',
  showEdit,
  showDelete,
  showView,
  showClaimEdit,
  showReverse,
  hasCreate,
  handleEdit,
  handleView,
  handleDelete,
  handleCreate,
  rootPath,
  columns,
  data,
  pages,
  rowClassName,
  resource,
  isGlobalFilter,
  isJobListGlobalFilter,
  isAddOptions,
  isAddUserList,
  handleUserClick,
  handleCustomerClick,
  isAddCustList,
  customPageSizeOptions,
  iscustomPageSizeOptions,
  isPagination,
  isShowingPageLength,
  paginationDiv,
  pagination,
  tableClass,
  loading,
  theadClass,
  searchByNames,
  hasExport,
  hasSearch,
  paginationData,
  onChangePagination,
  completeTransaction = false,
  statuses = [],
  hasStatus = false,
  pageSizeAtBottom = true,
  toolbar = null,
  rowName,
  rowClickable,
}) => {
  // While a new page/filter loads, keep the previous rows on screen (dimmed
  // under the spinner) so the card height doesn't collapse and jump.
  const lastData = useRef(data);
  if (data && data.length) lastData.current = data;
  const tableData =
    pageSizeAtBottom && loading && !(data && data.length)
      ? lastData.current || []
      : data;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: tableData,
      // defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        hiddenColumns: columns
          ?.filter((c) => c.hidden)
          ?.map((c) => c?.accessor),
        pageIndex: 0,
        pageSize: paginationData.pageSize,
        sortBy: [
          {
            desc: true,
          },
        ],
      },
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
  );
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [deleteModal, setDeleteModal] = useState(false);
  const [id, setId] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [status, setStatus] = useState('');
  const [searchKey, setSearchKey] = useState('');

  const generateSortingIndicator = (column) => {
    return column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : '';
  };

  const onChangeInSelect = (event) => {
    const pageSize = Number(event.target.value);
    const isSearch = searchKey && searchValue;
    const search = {};
    if (isSearch) search[searchKey] = searchValue;
    onChangePagination(
      { limit: pageSize, page: paginationData.pageNumber },
      search,
    );
    setPageSize(pageSize);
  };

  const onCreate = () => {
    if (handleCreate) {
      handleCreate();
    } else {
      navigate(`${rootPath || location.pathname}/create`);
    }
  };
  const onCompleteTransaction = () => {
    navigate(`${rootPath || location.pathname}/complete-transaction`);
  };

  const onEdit = (record) => {
    if (handleEdit) {
      handleEdit(record.original);
    } else {
      navigate(`${rootPath || location.pathname}/${record.original.id}/edit`);
    }
  };

  const onView = (record) => {
    if (handleView) {
      handleView(record.original);
    } else {
      navigate(`${rootPath || location.pathname}/${record.original.id}`);
    }
  };

  // Whole-row click: view when the table can view, else edit, else nothing.
  // Pages may force it on/off with `rowClickable`.
  const rowAction =
    handleView || showView ? onView : handleEdit || showEdit ? onEdit : null;
  const isRowClickable = rowClickable === undefined ? !!rowAction : rowClickable && !!rowAction;

  const [deleteRow, setDeleteRow] = useState(null);
  const onDelete = (record) => {
    setId(record?.original?.id || record?.original?.dependentId);
    setDeleteRow(record?.original || null);
    setDeleteModal(true);
  };

  // Best-effort human label for the row being deleted (override with `rowName`).
  const defaultRowName = (r) =>
    r?.name ||
    r?.groupName ||
    r?.fullName ||
    [r?.firstName, r?.lastName].filter(Boolean).join(' ') ||
    r?.transactionNumber ||
    r?.title ||
    r?.email ||
    null;
  const deleteName = deleteRow ? (rowName || defaultRowName)(deleteRow) : null;
  const resourceLabel = resource
    ? String(resource).split('/').pop().replace(/[-_]/g, ' ').toLowerCase()
    : 'record';

  const onReverse = async (record) => {
    await reverseApi(record?.original?.id).then((res) => {
      alert(res?.data);
    });
  };

  const deleteResource = async () => {
    if (handleDelete) {
      await handleDelete(id);
    } else {
      await delApi(id).then(() => { });
    }
    setDeleteModal(false);
  };

  // const [currentPage, setCurrentPage] = useState(paginationData?.pageNumber || 1);
  const { pageNumber, pageSize: limit } = paginationData;

  const indexOfLastItem = pageNumber * limit;
  const indexOfFirstItem = indexOfLastItem - limit;
  const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(data.length / limit);

  const renderPaginationLinks = () => {
    // First, last, and a window of pages around the current one (1 … 4 5 6 … 217)
    const window = 2;
    const pagesToShow = [];
    for (let i = 1; i <= pages; i++) {
      if (i === 1 || i === pages || Math.abs(i - pageNumber) <= window) {
        pagesToShow.push(i);
      }
    }

    return pagesToShow.map((pageNum, index) => (
      <React.Fragment key={pageNum}>
        {index > 0 && pageNum !== pagesToShow[index - 1] + 1 && (
          <PaginationItem disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        )}
        <PaginationItem active={pageNumber === pageNum}>
          <PaginationLink onClick={() => paginate(pageNum)}>
            {pageNum.toLocaleString()}
          </PaginationLink>
        </PaginationItem>
      </React.Fragment>
    ));
  };

  const paginate = (pageNumber) => {
    const isSearch = searchKey && searchValue;

    const search = {};
    if (isSearch) search[searchKey] = searchValue;
    onChangePagination(
      { limit: paginationData.pageSize, page: pageNumber },
      search,
    );
  };

  const makeDebounceSearch = useCallback(
    debounce((key, value) => {
      if (key && value) {
        onChangePagination(
          { limit: paginationData.pageSize, page: paginationData.pageNumber },
          {
            [key]: value,
          },
        );
      } else if (key && !value) {
        onChangePagination(
          { limit: paginationData.pageSize, page: paginationData.pageNumber },
          {},
        );
      }
    }, 600),
    [paginationData],
  );

  useEffect(() => {
    makeDebounceSearch(searchKey, searchValue);
  }, [searchValue]);

  const pageSizeSelect = iscustomPageSizeOptions && (
    <SelectField
      name="pageSize"
      value={pageSize}
      onChange={onChangeInSelect}
      searchable={false}
      options={[10, 25, 50, 100, 200].map((size) => ({
        value: size,
        label: `Show ${size}`,
      }))}
    />
  );

  return (
    <Fragment>
      {((iscustomPageSizeOptions && !pageSizeAtBottom) ||
        hasSearch ||
        toolbar ||
        completeTransaction ||
        hasCreate) && (
      <Row className={pageSizeAtBottom ? 'mb-3 align-items-center' : 'mb-2'}>
        {iscustomPageSizeOptions && !pageSizeAtBottom && (
          <Col md={customPageSizeOptions ? 3 : 2}>
            <SelectField
              name="pageSize"
              value={pageSize}
              onChange={onChangeInSelect}
              searchable={false}
              options={[10, 25, 50, 100, 200].map((size) => ({
                value: size,
                label: `Show ${size}`,
              }))}
            />
          </Col>
        )}

        {hasSearch && (
          <Col
            xs="12"
            md={pageSizeAtBottom ? 7 : 6}
            lg={pageSizeAtBottom ? 6 : undefined}
          >
            <Form
              autoComplete="off"
              {...(searchByNames && {
                initialValues: {
                  searchByName: searchByNames?.[0]?.value,
                },
              })}
            >
              <Row>
                {searchByNames && (
                  <Col xs="12" sm>
                    <FormGroup className="mb-2 mb-sm-0">
                      <SelectField
                        name="searchByName"
                        value={searchKey}
                        onChange={(e) => {
                          setSearchKey(e.target.value);
                        }}
                        className="search-selector"
                        placeholder="Search by:"
                      >
                        <option key="0" value="">
                          {`Select Search Field`}
                        </option>
                        {searchByNames.map(({ value, text }) => (
                          <option key={value} value={value}>
                            {`Search by: ${text}`}
                          </option>
                        ))}
                      </SelectField>
                    </FormGroup>
                  </Col>
                )}
                <Col xs="12" sm>
                  <FormGroup className="mb-2 mb-sm-0">
                    <Input
                      name="searchData"
                      placeholder="Search"
                      value={searchValue}
                      onChange={(e) => {
                        !searchKey && setSearchKey(searchByNames?.[0]?.value);
                        setSearchValue(e.target.value);
                      }}
                      // onKeyPress={(e) => {
                      //   if (e.key === 'Enter') {
                      //     handleSubmit(e);
                      //   }
                      // }}
                    />
                  </FormGroup>
                </Col>
                {
                  hasStatus && <Col xs="12" sm>
                    <FormGroup className="mb-2 mb-sm-0">
                      <SelectField
                        id="countyId"
                        name="countyId"
                        value={status}
                        onChange={(e) => {
                          const value = e.target.value
                          setStatus(value);
                          makeDebounceSearch('status', value);
                        }}
                      >
                        <option defaultValue value={''}>
                          Select Status...
                        </option>
                        {statuses &&
                          statuses?.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                      </SelectField>
                    </FormGroup>
                  </Col>
                }
              </Row>
            </Form>
          </Col>
        )}
        {toolbar && (
          <Col xs="12" md={7} lg={6} className="mb-2 mb-md-0">
            {toolbar}
          </Col>
        )}
        {pageSizeAtBottom && (completeTransaction || hasCreate) && (
          <Col xs="12" md className="ms-md-auto">
            <div className="d-flex justify-content-sm-end gap-2 flex-wrap">
              {completeTransaction && (
                <Button
                  type="button"
                  color="primary"
                  className="btn-rounded flex-fill flex-sm-grow-0"
                  onClick={onCompleteTransaction}
                >
                  <i className="mdi mdi-plus me-1" />
                  Add Payment
                </Button>
              )}
              {hasCreate && (
                <Button
                  type="button"
                  color="success"
                  className="btn-rounded flex-fill flex-sm-grow-0"
                  onClick={onCreate}
                >
                  <i
                    className={`mdi ${
                      completeTransaction ? 'mdi-send-outline' : 'mdi-plus'
                    } me-1`}
                  />
                  {addText}
                </Button>
              )}
            </div>
          </Col>
        )}
        {completeTransaction && !pageSizeAtBottom && (
          <Col sm="4">
            <div className="text-end">
              <Button
                type="button"
                color="primary"
                className="btn-rounded mb-2 me-2"
                onClick={onCompleteTransaction}
              >
                <i className="mdi mdi-plus me-1" />
                Add Payment
              </Button>
            </div>
          </Col>
        )}
        {hasCreate && !pageSizeAtBottom && (
          <Col sm="4">
            <div className="text-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded me-2"
                onClick={onCreate}
              >
                <i className="mdi mdi-plus me-1" />
                {addText}
              </Button>
            </div>
          </Col>
        )}
        {/* {isAddUserList && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="primary"
                className="btn mb-2 me-2"
                onClick={handleUserClick}
              >
                <i className="mdi mdi-plus-circle-outline me-1" />
                Create New User
              </Button>
            </div>
          </Col>
        )} */}
        {/* {isAddCustList && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded mb-2 me-2"
                onClick={handleCustomerClick}
              >
                <i className="mdi mdi-plus me-1" />
                New Customers
              </Button>
            </div>
          </Col>
        )} */}
      </Row>
      )}

      <div
        className={`table-responsive${pageSizeAtBottom ? ' position-relative' : ''}`}
        style={pageSizeAtBottom && loading ? { minHeight: 240 } : undefined}
      >
        {pageSizeAtBottom && loading && (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              background: 'rgba(255, 255, 255, 0.6)',
            }}
          >
            <Spinner color="primary" />
          </div>
        )}
        {loading && !pageSizeAtBottom ? (
          <Spinners />
        ) : (
          <Table
            {...getTableProps()}
            className={`table-standard ${tableClass || ''}${isRowClickable ? ' table-hover table-clickable' : ''}`}
            style={pageSizeAtBottom && loading ? { opacity: 0.45 } : undefined}
          >
            <thead className={theadClass}>
              {headerGroups.map((headerGroup) => (
                <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      key={column.id}
                      className={column.isSort ? 'sorting' : ''}
                    >
                      <div className="m-0" {...column.getSortByToggleProps()}>
                        {column.render('Header')}
                      </div>
                      {/* <Filter column={column} /> */}
                    </th>
                  ))}
                  <th className={''}>
                    <div className="m-0">{'ACTIONS'}</div>
                  </th>
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()}>
              {!loading && page.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="text-center text-muted py-4">
                    <i className="mdi mdi-database-search-outline font-size-18 d-block mb-1" />
                    No records found
                  </td>
                </tr>
              )}
              {page.map((row) => {
                prepareRow(row);
                return (
                  <Fragment key={row.getRowProps().key}>
                    <tr
                      onClick={
                        isRowClickable
                          ? (e) => {
                              // ignore clicks on controls inside the row (checkbox, action buttons, links)
                              if (e.target.closest('button, a, input, label, select, textarea')) return;
                              rowAction(row);
                            }
                          : undefined
                      }
                    >
                      {row.cells.map((cell) => {
                        return (
                          <td key={cell.id} {...cell.getCellProps()}>
                            {cell.render('Cell')}
                          </td>
                        );
                      })}
                      <td>
                        <div className="d-flex gap-3">
                          {showEdit && (
                            <Button
                              color="link"
                              className="text-success p-0 m-0"
                              onClick={() => onEdit(row)}
                            >
                              <i
                                className="mdi mdi-pencil font-size-18"
                                id="edittooltip"
                              />
                              <UncontrolledTooltip
                                placement="top"
                                target="edittooltip"
                              >
                                Edit
                              </UncontrolledTooltip>
                            </Button>
                          )}
                          {showClaimEdit && row?.original?.status === 'Revise' && (
                            <Button
                              color="link"
                              className="text-success p-0 m-0"
                              onClick={() => onEdit(row)}
                            >
                              <i
                                className="mdi mdi-pencil font-size-18"
                                id="edittooltip"
                              />
                              <UncontrolledTooltip
                                placement="top"
                                target="edittooltip"
                              >
                                Edit
                              </UncontrolledTooltip>
                            </Button>
                          )}
                          {showView && (
                            <Button
                              color="link"
                              className="text-primary p-0 m-0"
                              onClick={() => onView(row)}
                            >
                              <i
                                className="mdi mdi-eye font-size-18"
                                id="viewtooltip"
                              />
                              <UncontrolledTooltip
                                placement="top"
                                target="viewtooltip"
                              >
                                View
                              </UncontrolledTooltip>
                            </Button>
                          )}
                          {showDelete && (
                            <Button
                              color="link"
                              className="text-danger p-0 m-0"
                              onClick={() => onDelete(row)}
                            >
                              <i
                                className="mdi mdi-delete font-size-18"
                                id="deletetooltip"
                              />
                              <UncontrolledTooltip
                                placement="top"
                                target="deletetooltip"
                              >
                                Delete
                              </UncontrolledTooltip>
                            </Button>
                          )}
                          {showReverse && (
                            <Button
                              color="link"
                              className="text-danger p-0 m-0"
                              onClick={() => onReverse(row)}
                            >
                              <i
                                className="mdi mdi-delete font-size-18"
                                id="reversetooltip"
                              />
                              <UncontrolledTooltip
                                placement="top"
                                target="reversetooltip"
                              >
                                Reverse
                              </UncontrolledTooltip>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
        )}
      </div>

      <div
        className={
          pageSizeAtBottom
            ? 'd-flex flex-wrap justify-content-between align-items-center gap-3 mt-3'
            : undefined
        }
        hidden={!(pages > 1) && !(pageSizeAtBottom && iscustomPageSizeOptions)}
      >
      {pageSizeAtBottom && (
        <div style={{ width: '9rem' }}>{pageSizeSelect}</div>
      )}
      {pages > 1 && (
      <Pagination
        listClassName={pageSizeAtBottom ? 'pagination-rounded mb-0' : undefined}
      >
        <PaginationItem disabled={pageNumber <= 0}>
          <PaginationLink first onClick={() => paginate(1)} />
        </PaginationItem>
        <PaginationItem disabled={pageNumber === 1}>
          <PaginationLink previous onClick={() => paginate(pageNumber - 1)} />
        </PaginationItem>

        {renderPaginationLinks()}

        <PaginationItem disabled={pageNumber === pages}>
          <PaginationLink next onClick={() => paginate(pageNumber + 1)} />
        </PaginationItem>
        <PaginationItem disabled={pageNumber === pages}>
          <PaginationLink last onClick={() => paginate(pages)} />
        </PaginationItem>
      </Pagination>
      )}
      </div>

      {/* {
        isPagination && (
          <Row className="justify-content-between align-items-center">
            {isShowingPageLength && <div className="col-sm">
              <div className="text-muted">Showing <span className="fw-semibold">{page.length}</span> of <span className="fw-semibold">{data.length}</span> entries</div>
            </div>}
            <div className={paginationDiv}>
              <ul className={pagination}>
                <li className={`page-item ${!canPreviousPage ? "disabled" : ''}`}>
                  <Link to="#" className="page-link" onClick={previousPage}>
                    <i className="mdi mdi-chevron-left"></i>
                  </Link>
                </li>
                {pageOptions.map((item, key) => (
                  <React.Fragment key={key}>
                    <li className={pageIndex === item ? "page-item active" : "page-item"}>
                      <Link to="#" className="page-link" onClick={() => gotoPage(item)}>{item + 1}</Link>
                    </li>
                  </React.Fragment>
                ))}
                <li className={`page-item ${!canNextPage ? "disabled" : ''}`}>
                  <Link to="#" className="page-link" onClick={nextPage}>
                    <i className="mdi mdi-chevron-right"></i>
                  </Link>
                </li>
              </ul>
            </div>
          </Row>
        )
      } */}
      <DeleteModal
        id={id}
        name={deleteName}
        title={`Delete ${resourceLabel}`}
        show={deleteModal}
        onDeleteClick={deleteResource}
        onCloseClick={() => setDeleteModal(false)}
      />
    </Fragment>
  );
};

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default TableContainer;
